import { useCallback, useMemo, useState } from 'react';
import { CommonProperties } from '../common/common.interfaces';
import BlobModalComponent from '../blobs/BlobModalComponent';
import { CUSTOM_JSON_THEME } from '../common/common.constants';
import JSONPretty from 'react-json-pretty';
import { Tab, TabList, TabPanel, Tabs } from 'react-tabs';
import WayfairMcfInProgress from './components/WayfairMcfInProgress';
import { Check2All, ClockHistory, ExclamationDiamond, X } from 'react-bootstrap-icons';
import { useUserContext } from '../user/UserContext';
import WayfairMcfErrors from './components/WayfairMcfErrors';
import WayfairMcfCancelled from './components/WayfairMcfCancelled';
import WayfairMcfComplete from './components/WayfairMcfComplete';
import { Button, Form } from 'react-bootstrap';
import { useLoading } from '../loading/LoadingContext';
import { OrdersMcfTableRow } from '../common/common.table.interfaces';
import { apiFetch } from '../utility/fetch';
import { API_PROD_URL } from '..';
import { notify } from '../notify/notify';
import WayfairMcfSearchModal from './components/WayfairMcfSearchModal';

interface WayfairMcfProps extends CommonProperties {}

export default function WayfairMcf(props: WayfairMcfProps){
  const {toggleLoading, changeStep, changeSubStep} = useLoading()
  const {userData} = useUserContext()

  const [searchText, setSearchText]: [string, any] = useState('')
  const [searchTitle, setSearchTitle]: [string, any] = useState('')
  const [searchContent, setSearchContent]: [OrdersMcfTableRow, any] = useState(undefined)
  const [searchVisible, setSearchVisible] = useState(false)

  const [modalTitle, setModalTitle] = useState('')
  const [modalContent, setModalContent] = useState('')
  const [modalVisible, setModalVisible] = useState(false)

  const handleShowContent = useCallback((e) => {
    const {title, ordernumber, fulfillmentrequestid, content} = e.currentTarget.dataset
    setModalTitle(`${title} - ${ordernumber} - ${fulfillmentrequestid}`)
    setModalContent(content)
    setModalVisible(true)
  }, [])

  const handleSearchTextChanged = useCallback((e) => {
    setSearchText(e.currentTarget.value)
  }, [])

  const handleSearchClicked = useCallback(async (e) => {
    if(!searchText || searchText.trim() === ''){
      return
    }
    changeStep('Wayfair Multi-Channel Fulfillments')
    changeSubStep('Loading in progress orders')
    toggleLoading(true)

    const response = await apiFetch<OrdersMcfTableRow[]>({
      endpoint: 'azureTables',
      method: 'GET',
      accessToken: userData.accessToken,
      baseUrlOverride: API_PROD_URL,
      params: [
        {key: 'table', value: 'ordersMcf'},
        {key: 'filter', value: `(PartitionKey eq 'Wayfair-MCF-CA' or PartitionKey eq 'Wayfair-MCF-US') and orderNumber eq '${searchText}'`}
      ]
    })
    if(response.body.length === 0){
      notify('warning', `No Wayfair MCF orders found in Azure with that order number!`)
      toggleLoading(false)
      return
    }
    const row = response.body[0]
    setSearchTitle(`${row.orderNumber} - ${row.fulfillmentOrderRequestId ?? 'NOT CREATED YET'}`)
    setSearchContent(row)
    setSearchVisible(true)

    toggleLoading(false)
  }, [searchText])

  const handleRowClicked = useCallback((row) => {
    setSearchTitle(`${row.orderNumber} - ${row.fulfillmentOrderRequestId ?? 'NOT CREATED YET'}`)
    setSearchContent(row)
    setSearchVisible(true)
  }, [])

  const view = useMemo(() => {
    return <div>
      <WayfairMcfSearchModal hideModal={() => {setSearchVisible(false)}} row={searchContent} title={searchTitle} visible={searchVisible} />
      <BlobModalComponent raw={modalContent} blobData={<JSONPretty data={modalContent} theme={CUSTOM_JSON_THEME} style={{fontSize: '1.5em'}} />} blobName={modalTitle} closeModal={() => {setModalVisible(false)}} modalVisible={modalVisible === true}/>
      <div id='top'></div>
      <div className='beacon-card'>
        <table className='beacon-table'>
          <tbody>
            <tr>
              <td><span className='beacon-header'>Wayfair Multi-Channel Fulfillments</span></td>
            </tr>
            <tr>
              <td><span className='beacon-label'>View and manage Wayfair MCF orders that are in process, rejected, cancelled, or complete.</span></td>
            </tr>
          </tbody>
        </table>
      </div>
      <br/>
      <div className='beacon-card'>
        <span className='beacon-label'>Look up information by order number.</span>
        <Form.Control 
          style={{width: '150px'}}
          placeholder='Enter order number...'
          onChange={handleSearchTextChanged}
          value={searchText}
        />
        <Button onClick={handleSearchClicked} variant='success'>SEARCH</Button>
      </div>
      <br/>
      <Tabs>
        <TabList>
          <Tab key='in-progress-tab'><span className='beacon-header'><ClockHistory size={20} color='orange' />&nbsp;IN PROGRESS</span></Tab>
          <Tab key='error-tab'><span className='beacon-header'><ExclamationDiamond size={20} color='red' />&nbsp;REJECTED / UNKNOWN</span></Tab>
          <Tab key='cancelled-tab'><span className='beacon-header'><X size={25} color='red'/>&nbsp;CANCELLED</span></Tab>
          <Tab key='complete-tab'><span className='beacon-header'><Check2All size={20} color='green'/>&nbsp;COMPLETE</span></Tab>
        </TabList>
        <TabPanel key='in-progress-tab-panel'>
          <WayfairMcfInProgress handleRowClicked={handleRowClicked} handleShowContent={handleShowContent}/>
        </TabPanel>
        <TabPanel key='error-tab-panel'>
          <WayfairMcfErrors handleRowClicked={handleRowClicked} handleShowContent={handleShowContent}/>
        </TabPanel>
        <TabPanel key='cancelled-tab-panel'>
          <WayfairMcfCancelled handleRowClicked={handleRowClicked} handleShowContent={handleShowContent}/>
        </TabPanel>
        <TabPanel key='complete-tab-panel'>
          <WayfairMcfComplete handleRowClicked={handleRowClicked} handleShowContent={handleShowContent}/>
        </TabPanel>
      </Tabs>

    </div>
  }, [modalTitle, modalContent, modalVisible, searchContent, searchTitle, searchVisible, searchText])

  return view
}